/**
 * Converts a background_data JSON object into CSS variable string
 * to be appended to a card's theme CSS.
 *
 * @param {Object|null} backgroundData - The background_data from the card
 * @param {string} s3Bucket - S3 bucket URL
 * @param {string} s3Region - S3 region
 * @returns {string|null} CSS variables string or null if no custom background
 */
export function backgroundDataToCssVars(backgroundData, s3Bucket, s3Region) {
    if (!backgroundData || !backgroundData.type) return null;

    let css = '';

    switch (backgroundData.type) {
    case 'solid':
        css += '--card_bg: ' + backgroundData.colour + ';';
        css += '--bg_image: none;';
        break;

    case 'gradient':
        css += '--card_bg: transparent;';
        css += '--bg_image: ' + buildGradientCss(backgroundData) + ';';
        break;

    case 'image':
        css += '--card_bg: transparent;';
        css += '--bg_image: url("' + buildS3Url(backgroundData.path, s3Bucket, s3Region) + '");';
        css += '--bg_size: ' + buildBackgroundSize(backgroundData) + ';';
        css += '--bg_position: center ' + (backgroundData.position || 'center') + ';';
        css += '--bg_repeat: no-repeat;';
        break;

    case 'hybrid': {
        const imageUrl = buildS3Url(backgroundData.path, s3Bucket, s3Region);
        const overlay = buildOverlayCss(backgroundData.overlay);
        const imgSize = buildBackgroundSize(backgroundData);
        const imgPosition = 'center ' + (backgroundData.position || 'center');
        css += '--card_bg: transparent;';
        css += '--bg_image: ' + overlay + ', url("' + imageUrl + '");';
        css += '--bg_size: cover, ' + imgSize + ';';
        css += '--bg_position: center, ' + imgPosition + ';';
        css += '--bg_repeat: no-repeat, no-repeat;';
        break;
    }

    default:
        return null;
    }

    return css;
}

/**
 * Builds a CSS gradient string from gradient data.
 *
 * @param {Object} data - Object with gradientType, angle, colours
 * @returns {string} CSS gradient function
 */
export function buildGradientCss(data) {
    const stops = (data.colours || [])
        .map(c => c.hex + ' ' + c.position + '%')
        .join(', ');

    switch (data.gradientType) {
    case 'radial':
        return 'radial-gradient(circle, ' + stops + ')';
    case 'conic':
        return 'conic-gradient(from ' + (data.angle || 0) + 'deg, ' + stops + ')';
    case 'linear':
    default:
        return 'linear-gradient(' + (data.angle || 0) + 'deg, ' + stops + ')';
    }
}

/**
 * Builds the background-size CSS value.
 *
 * @param {Object} data - Object with size and height properties
 * @returns {string} CSS background-size value
 */
function buildBackgroundSize(data) {
    if (data.size === 'cover' || data.size === 'contain') {
        return data.size;
    }
    // Auto/custom: use height percentage
    const height = data.height || 100;
    return '100% ' + height + '%';
}

/**
 * Builds a CSS overlay layer for hybrid backgrounds.
 *
 * @param {Object} overlay - Overlay configuration
 * @returns {string} CSS gradient function for the overlay
 */
function buildOverlayCss(overlay) {
    if (!overlay) return 'linear-gradient(transparent, transparent)';

    if (overlay.type === 'solid') {
        const colour = overlay.colour || '#000000';
        const opacity = overlay.opacity !== undefined ? overlay.opacity : 0.5;
        const rgba = hexToRgba(colour, opacity);
        return 'linear-gradient(' + rgba + ', ' + rgba + ')';
    }

    if (overlay.type === 'gradient') {
        return buildGradientCss(overlay);
    }

    return 'linear-gradient(transparent, transparent)';
}

/**
 * Converts a hex colour + opacity to an rgba string.
 *
 * @param {string} hex - Hex colour (e.g. "#000000")
 * @param {number} opacity - Opacity value 0-1
 * @returns {string} rgba() string
 */
function hexToRgba(hex, opacity) {
    const cleaned = hex.replace('#', '');
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + opacity + ')';
}

/**
 * Render a resolved background_data object as an inline `background:` CSS rule.
 *
 * @param {Object|null} bg - Resolved background_data object
 * @param {string} s3Bucket
 * @param {string} s3Region
 * @returns {string} CSS string ending in a semicolon, or empty string
 */
export function renderBackgroundDataCss(bg, s3Bucket, s3Region) {
    if (!bg || !bg.type) return '';

    if (bg.type === 'solid') {
        return 'background: ' + bg.colour + ';';
    }

    if (bg.type === 'gradient') {
        return 'background: ' + buildGradientCss(bg) + ';';
    }

    if (bg.type === 'image') {
        const url = buildS3Url(bg.path, s3Bucket, s3Region);
        const size = buildBackgroundSize(bg);
        return 'background: url("' + url + '") center ' + (bg.position || 'center') + ' / ' + size + ' no-repeat;';
    }

    if (bg.type === 'hybrid') {
        const url = buildS3Url(bg.path, s3Bucket, s3Region);
        const size = buildBackgroundSize(bg);
        const overlay = buildOverlayCss(bg.overlay);
        return 'background: ' + overlay + ', url("' + url + '") center ' + (bg.position || 'center') + ' / ' + size + ' no-repeat;';
    }

    return '';
}

/**
 * Builds the full S3 URL from a path.
 *
 * @param {string} path - S3 path (e.g. "/folderUUID/fileUUID")
 * @param {string} s3Bucket - S3 bucket URL
 * @param {string} s3Region - S3 region
 * @returns {string} Full S3 URL
 */
function buildS3Url(path, s3Bucket, s3Region) {
    if (!s3Bucket) {
        // SCORM packages run with no bucket so paths point at the local
        // assets/ directory rewritten by PlaylistAdminService::update_scorm_playlist.
        return path;
    }
    return 'https://' + s3Bucket + '.s3.' + s3Region + '.amazonaws.com' + path;
}
